import isNil from "lodash/isNil";
import some from "lodash/some";
import sum from "lodash/sum";
import values from "lodash/values";
import { useEngagementFeedbackParticipants } from "PFApp/activities/hooks";
import { useActivityMatches } from "PFApp/activities/show/hooks/use_activity_matches";
import { useActivityShortlists } from "PFApp/activities/show/hooks/use_activity_shortlists";
import { useCurrentActivity } from "PFApp/activities/show/hooks/use_current_activity";
import { useActivityVacancies } from "PFCore/hooks/queries/activity";
import { useInfiniteActivityWatchers } from "PFCore/hooks/queries/activity/use_activity_watchers";
import { useEffect, useMemo, useState } from "react";

export const useActivityPageCounters = () => {
  const activity = useCurrentActivity();
  const { shortlistsMeta, suspendedShortlistsMeta } = useActivityShortlists();
  const { matchesMeta } = useActivityMatches();

  const { data: vacancies } = useActivityVacancies({ activityId: activity.id });
  const { data: feedbackParticipants } = useEngagementFeedbackParticipants(activity);

  const pendingFeedbacks =
    feedbackParticipants?.entries?.filter((participant) =>
      some(participant.feedback, ({ status }) => status === "pending")
    ) || [];
  const feedbackCount = pendingFeedbacks?.length === 0 ? undefined : pendingFeedbacks?.length;

  const { data: interested } = useInfiniteActivityWatchers(activity, 0);

  const [interestsCount, setInterestsCount] = useState<number | undefined>(interested?.pages[0].meta.total);

  useEffect(() => {
    if (interested && isNil(interestsCount)) {
      setInterestsCount(interested.pages[0].meta.total);
    }
  }, [interested, interestsCount]);

  const isClosed = activity.state === "complete";
  const counters = useMemo(
    () => ({
      shortlist: Math.max((shortlistsMeta?.total || 0) + (suspendedShortlistsMeta?.total || 0), 0),
      matches: isClosed ? undefined : matchesMeta.total,
      roles: sum(values(activity.children_workflow_states_tally)),
      vacancies: vacancies?.meta.total,
      interested: interestsCount,
      feedback: feedbackCount
    }),
    [
      shortlistsMeta?.total,
      suspendedShortlistsMeta?.total,
      matchesMeta.total,
      activity.children_workflow_states_tally,
      vacancies?.meta.total,
      interestsCount,
      feedbackCount,
      isClosed
    ]
  );

  return {
    counters,
    setInterestsCount
  };
};
